import { useEffect, useMemo, useState } from 'react';
import { readError } from '../../app/api';
import { formatDateTime } from '../../shared/date-time';
import { loadAllInstallationSessions, loadAllInstallations, loadCompleteSession } from './analytics-api';
import type { AnalysisMode, AnalysisSessionDataset, AnalysisTab } from './analysis-types';
import { AnalysisDomainView } from './DomainViews';
import { formatCountryName, SessionCountry } from './GeoUi';
import { PlatformBadge, PlatformBadgeList, formatPlatformName, formatPlatformNames, listKnownPlatformCodes } from './PlatformUi';
import type { AnalyticsEventRecord, InstallationSummary, SessionSummary } from './types';

const MAXIMUM_COMPARISON_SESSIONS = 4;

interface AnalysisLocationState {
  mode: AnalysisMode;
  tab: AnalysisTab;
  platformCodes: number[];
  installationId: string | null;
  sessionIds: string[];
}

interface AnalysisLocationUpdate {
  mode?: AnalysisMode;
  tab?: AnalysisTab;
  platformCodes?: number[];
  installationId?: string | null;
  sessionIds?: string[];
}

export function AnalysisWorkspace(props: { environmentId: string }): React.JSX.Element {
  const initialLocation = readAnalysisLocation();
  const [mode, setMode] = useState<AnalysisMode>(initialLocation.mode);
  const [tab, setTab] = useState<AnalysisTab>(initialLocation.tab);
  const [installations, setInstallations] = useState<InstallationSummary[]>([]);
  const [selectedPlatformCodes, setSelectedPlatformCodes] = useState<number[]>(initialLocation.platformCodes);
  const [selectedInstallationId, setSelectedInstallationId] = useState<string | null>(initialLocation.installationId);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>(initialLocation.sessionIds);
  const [datasets, setDatasets] = useState<AnalysisSessionDataset[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<AnalyticsEventRecord | null>(null);
  const [loadingInstallations, setLoadingInstallations] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [selectionMessage, setSelectionMessage] = useState('');
  const [error, setError] = useState('');
  const matchingInstallations = useMemo(
    () => filterInstallationsByPlatform(installations, selectedPlatformCodes),
    [installations, selectedPlatformCodes],
  );

  useEffect(() => {
    let active = true;
    setLoadingInstallations(true);
    setInstallations([]);
    setSessions([]);
    setDatasets([]);
    setSelectedEvent(null);
    setError('');
    const requestedLocation = readAnalysisLocation();
    setSelectedPlatformCodes(requestedLocation.platformCodes);
    loadAllInstallations(props.environmentId)
      .then((loadedInstallations) => {
        if (!active) return;
        const selectableInstallations = filterInstallationsByPlatform(loadedInstallations, requestedLocation.platformCodes);
        const requestedInstallationId = requestedLocation.installationId;
        const selectedInstallation = selectableInstallations.find((installation) => installation.installationId === requestedInstallationId)
          ?? selectableInstallations[0];
        const nextInstallationId = selectedInstallation?.installationId ?? null;
        setInstallations(loadedInstallations);
        setSelectedInstallationId(nextInstallationId);
        if (nextInstallationId !== requestedInstallationId) {
          setSelectedSessionIds([]);
          writeAnalysisLocation({ installationId: nextInstallationId, sessionIds: [] }, 'replace');
        }
      })
      .catch((caught: unknown) => { if (active) setError(readError(caught)); })
      .finally(() => { if (active) setLoadingInstallations(false); });
    return () => { active = false; };
  }, [props.environmentId]);

  useEffect(() => {
    let active = true;
    setSessions([]);
    setDatasets([]);
    setSelectedEvent(null);
    setSelectionMessage('');
    const installationIsAvailable = matchingInstallations.some((installation) => installation.installationId === selectedInstallationId);
    if (selectedInstallationId === null || !installationIsAvailable) {
      setLoadingSessions(false);
      setSelectedSessionIds([]);
      return () => { active = false; };
    }
    setLoadingSessions(true);
    setError('');
    loadAllInstallationSessions(props.environmentId, selectedInstallationId, selectedPlatformCodes)
      .then((loadedSessions) => {
        if (!active) return;
        const requestedSessionIds = readAnalysisLocation().sessionIds;
        const availableIds = new Set(loadedSessions.map(readSessionId));
        const validRequestedIds = requestedSessionIds.filter((sessionId) => availableIds.has(sessionId));
        const maximum = mode === 'session' ? 1 : MAXIMUM_COMPARISON_SESSIONS;
        const defaultSessionId = loadedSessions[0]?.sessionId;
        const nextSessionIds = validRequestedIds.length > 0
          ? validRequestedIds.slice(0, maximum)
          : defaultSessionId === undefined ? [] : [defaultSessionId];
        setSessions(loadedSessions);
        setSelectedSessionIds(nextSessionIds);
        writeAnalysisLocation({ sessionIds: nextSessionIds }, 'replace');
      })
      .catch((caught: unknown) => { if (active) setError(readError(caught)); })
      .finally(() => { if (active) setLoadingSessions(false); });
    return () => { active = false; };
  }, [matchingInstallations, props.environmentId, selectedInstallationId, selectedPlatformCodes]);

  useEffect(() => {
    let active = true;
    const selectedSummaries = selectedSessionIds
      .map((sessionId) => sessions.find((session) => session.sessionId === sessionId))
      .filter(isSessionSummary);
    setDatasets([]);
    setSelectedEvent(null);
    if (selectedSummaries.length === 0) {
      setLoadingEvents(false);
      return () => { active = false; };
    }
    setLoadingEvents(true);
    setError('');
    Promise.all(selectedSummaries.map(async (summary) => ({
      summary,
      events: await loadCompleteSession(props.environmentId, summary.sessionId),
    })))
      .then((loadedDatasets) => { if (active) setDatasets(loadedDatasets); })
      .catch((caught: unknown) => { if (active) setError(readError(caught)); })
      .finally(() => { if (active) setLoadingEvents(false); });
    return () => { active = false; };
  }, [props.environmentId, sessions, selectedSessionIds]);

  useEffect(() => {
    function restoreLocation(): void {
      const location = readAnalysisLocation();
      setMode(location.mode);
      setTab(location.tab);
      setSelectedPlatformCodes(location.platformCodes);
      const selectableInstallations = filterInstallationsByPlatform(installations, location.platformCodes);
      const selectedInstallation = selectableInstallations.find((installation) => installation.installationId === location.installationId)
        ?? selectableInstallations[0];
      const nextInstallationId = selectedInstallation?.installationId ?? null;
      setSelectedInstallationId(nextInstallationId);
      setSelectedSessionIds(nextInstallationId === location.installationId ? location.sessionIds : []);
      setSelectedEvent(null);
    }
    window.addEventListener('popstate', restoreLocation);
    return () => { window.removeEventListener('popstate', restoreLocation); };
  }, [installations]);

  const selectedInstallation = matchingInstallations.find((installation) => installation.installationId === selectedInstallationId);
  const selectedSessions = selectedSessionIds
    .map((sessionId) => sessions.find((session) => session.sessionId === sessionId))
    .filter(isSessionSummary);
  const eventLookup = useMemo(() => buildEventLookup(datasets), [datasets]);

  return (
    <section className="analytics-page analysis-workspace">
      <article className="surface analysis-scope">
        <div className="surface-heading"><div><span className="eyebrow">Analysis scope</span><h2>Choose what the charts describe</h2></div><small>Unrelated sessions are never joined.</small></div>
        <AnalysisPlatformFilter installations={installations} selectedCodes={selectedPlatformCodes} onChange={changePlatformCodes} />
        <div className="analysis-scope-controls">
          <div className="analysis-mode-toggle" aria-label="Analysis mode">
            <button className={mode === 'session' ? 'active' : ''} onClick={showSessionMode}>Single session</button>
            <button className={mode === 'comparison' ? 'active' : ''} onClick={showComparisonMode}>Compare sessions</button>
          </div>
          <label>Install<select value={selectedInstallationId ?? ''} onChange={changeInstallation} disabled={loadingInstallations || matchingInstallations.length === 0}>
            {matchingInstallations.map(renderInstallationOption)}
          </select></label>
          {mode === 'session' && <label>Session<select value={selectedSessionIds[0] ?? ''} onChange={changeSingleSession} disabled={loadingSessions || sessions.length === 0}>
            {sessions.map(renderSessionOption)}
          </select></label>}
        </div>
        {mode === 'comparison' && <div className="analysis-session-choices">
          <div><strong>Sessions to compare</strong><small>Select up to {MAXIMUM_COMPARISON_SESSIONS}; charts align by session elapsed time.</small></div>
          <div className="analysis-session-choice-grid">{sessions.map(renderComparisonSession)}</div>
          {selectionMessage !== '' && <p className="analysis-selection-message">{selectionMessage}</p>}
        </div>}
      </article>

      {error !== '' && <div className="error-banner">{error}</div>}
      <AnalysisContext installation={selectedInstallation} sessions={selectedSessions} loading={loadingInstallations || loadingSessions || loadingEvents} emptyText={selectedPlatformCodes.length === 0 ? 'No tracked installs are available in this environment.' : 'No installs match the selected platforms.'} />

      <nav className="analysis-tabs" aria-label="Analysis area">
        <button className={tab === 'colony' ? 'active' : ''} onClick={showColony}>Colony</button>
        <button className={tab === 'economy' ? 'active' : ''} onClick={showEconomy}>Economy</button>
        <button className={tab === 'construction' ? 'active' : ''} onClick={showConstruction}>Construction</button>
        <button className={tab === 'logistics' ? 'active' : ''} onClick={showLogistics}>Logistics</button>
      </nav>

      {loadingEvents && datasets.length === 0 && <div className="surface empty">Loading complete session data…</div>}
      {!loadingInstallations && matchingInstallations.length === 0 && <div className="surface empty">Choose another platform filter to continue.</div>}
      {!loadingEvents && matchingInstallations.length > 0 && selectedSessionIds.length === 0 && <div className="surface empty">Choose at least one session to analyze.</div>}
      {!loadingEvents && selectedSessionIds.length > 0 && datasets.length === 0 && error === '' && <div className="surface empty">The selected sessions could not be loaded.</div>}
      {datasets.length > 0 && <AnalysisDomainView tab={tab} mode={mode} datasets={datasets} eventLookup={eventLookup} selectedEvent={selectedEvent} onSelectEvent={setSelectedEvent} />}
    </section>
  );

  function showSessionMode(): void {
    const retainedSessionId = selectedSessionIds[0] ?? sessions[0]?.sessionId;
    const nextSessionIds = retainedSessionId === undefined ? [] : [retainedSessionId];
    setMode('session');
    setSelectedSessionIds(nextSessionIds);
    setSelectionMessage('');
    setSelectedEvent(null);
    writeAnalysisLocation({ mode: 'session', sessionIds: nextSessionIds }, 'push');
  }

  function showComparisonMode(): void {
    setMode('comparison');
    setSelectionMessage('');
    setSelectedEvent(null);
    writeAnalysisLocation({ mode: 'comparison' }, 'push');
  }

  function changePlatformCodes(platformCodes: number[]): void {
    const selectableInstallations = filterInstallationsByPlatform(installations, platformCodes);
    const retainedInstallation = selectableInstallations.find((installation) => installation.installationId === selectedInstallationId);
    const nextInstallationId = retainedInstallation?.installationId ?? selectableInstallations[0]?.installationId ?? null;
    setSelectedPlatformCodes(platformCodes);
    setSelectedInstallationId(nextInstallationId);
    setSelectedSessionIds([]);
    setSessions([]);
    setDatasets([]);
    setSelectionMessage('');
    setSelectedEvent(null);
    writeAnalysisLocation({ platformCodes, installationId: nextInstallationId, sessionIds: [] }, 'push');
  }

  function changeInstallation(event: React.ChangeEvent<HTMLSelectElement>): void {
    const installationId = event.target.value;
    setSelectedInstallationId(installationId);
    setSelectedSessionIds([]);
    setSelectionMessage('');
    setSelectedEvent(null);
    writeAnalysisLocation({ installationId, sessionIds: [] }, 'push');
  }

  function changeSingleSession(event: React.ChangeEvent<HTMLSelectElement>): void {
    const sessionIds = event.target.value === '' ? [] : [event.target.value];
    setSelectedSessionIds(sessionIds);
    setSelectedEvent(null);
    writeAnalysisLocation({ sessionIds }, 'push');
  }

  function renderComparisonSession(session: SessionSummary): React.JSX.Element {
    const selected = selectedSessionIds.includes(session.sessionId);
    const disabled = !selected && selectedSessionIds.length >= MAXIMUM_COMPARISON_SESSIONS;
    return <ComparisonSessionChoice key={session.sessionId} session={session} selected={selected} disabled={disabled} onToggle={toggleComparisonSession} />;
  }

  function toggleComparisonSession(sessionId: string): void {
    const selected = selectedSessionIds.includes(sessionId);
    if (!selected && selectedSessionIds.length >= MAXIMUM_COMPARISON_SESSIONS) {
      setSelectionMessage(`Select no more than ${MAXIMUM_COMPARISON_SESSIONS} sessions so the comparison remains readable.`);
      return;
    }
    const nextSessionIds = selected
      ? selectedSessionIds.filter((selectedId) => selectedId !== sessionId)
      : [...selectedSessionIds, sessionId];
    setSelectedSessionIds(nextSessionIds);
    setSelectionMessage('');
    setSelectedEvent(null);
    writeAnalysisLocation({ sessionIds: nextSessionIds }, 'push');
  }

  function showColony(): void { selectTab('colony'); }
  function showEconomy(): void { selectTab('economy'); }
  function showConstruction(): void { selectTab('construction'); }
  function showLogistics(): void { selectTab('logistics'); }

  function selectTab(nextTab: AnalysisTab): void {
    setTab(nextTab);
    setSelectedEvent(null);
    writeAnalysisLocation({ tab: nextTab }, 'push');
  }
}

function AnalysisPlatformFilter(props: {
  installations: InstallationSummary[];
  selectedCodes: number[];
  onChange: (platformCodes: number[]) => void;
}): React.JSX.Element {
  const platformCodes = collectPlatformFilterCodes(props.installations, props.selectedCodes);
  const matchingCount = filterInstallationsByPlatform(props.installations, props.selectedCodes).length;

  function showAll(): void {
    props.onChange([]);
  }

  function togglePlatform(platformCode: number): void {
    if (props.selectedCodes.includes(platformCode)) {
      const remainingCodes = props.selectedCodes.filter((selectedCode) => selectedCode !== platformCode);
      props.onChange(remainingCodes);
      return;
    }
    const nextCodes = props.selectedCodes.length === 0
      ? [platformCode]
      : [...props.selectedCodes, platformCode].sort(sortNumbers);
    props.onChange(nextCodes);
  }

  return <section className="analysis-platform-filter" aria-label="Platforms included in analysis">
    <div><strong>Platforms</strong><small>{describePlatformFilter(props.selectedCodes, matchingCount, props.installations.length)}</small></div>
    <div className="analysis-platform-options">
      <button className={props.selectedCodes.length === 0 ? 'active' : ''} aria-pressed={props.selectedCodes.length === 0} onClick={showAll}><span>All</span><small>{props.installations.length}</small></button>
      {platformCodes.map(renderPlatformOption)}
    </div>
  </section>;

  function renderPlatformOption(platformCode: number): React.JSX.Element {
    const installCount = countInstallationsForPlatform(props.installations, platformCode);
    return <AnalysisPlatformOption key={platformCode} platformCode={platformCode} installCount={installCount} selected={props.selectedCodes.includes(platformCode)} onToggle={togglePlatform} />;
  }
}

function AnalysisPlatformOption(props: {
  platformCode: number;
  installCount: number;
  selected: boolean;
  onToggle: (platformCode: number) => void;
}): React.JSX.Element {
  function toggle(): void {
    props.onToggle(props.platformCode);
  }
  return <button className={props.selected ? 'active' : ''} aria-pressed={props.selected} onClick={toggle} disabled={props.installCount === 0 && !props.selected}>
    <PlatformBadge code={props.platformCode} /><small>{props.installCount}</small>
  </button>;
}

function AnalysisContext(props: {
  installation: InstallationSummary | undefined;
  sessions: SessionSummary[];
  loading: boolean;
  emptyText: string;
}): React.JSX.Element {
  if (props.loading && props.sessions.length === 0) {
    return <div className="analysis-context surface">Loading scope…</div>;
  }
  if (props.installation === undefined) {
    return <div className="analysis-context surface">{props.emptyText}</div>;
  }
  return <article className="analysis-context surface">
    <div><span className="eyebrow">Install</span><code title={props.installation.installationId}>{shortId(props.installation.installationId)}</code><SessionCountry countryCode={props.installation.countryCode} countryCodes={props.installation.countryCodes} /><PlatformBadgeList codes={props.installation.platformCodes} /><small>{props.installation.sessionCount} sessions · {formatNumber(props.installation.eventCount)} events</small></div>
    {props.sessions.map(renderSessionContext)}
  </article>;
}

function ComparisonSessionChoice(props: {
  session: SessionSummary;
  selected: boolean;
  disabled: boolean;
  onToggle: (sessionId: string) => void;
}): React.JSX.Element {
  function toggle(): void {
    props.onToggle(props.session.sessionId);
  }
  return <label className={props.selected ? 'analysis-session-choice selected' : 'analysis-session-choice'}>
    <input type="checkbox" checked={props.selected} disabled={props.disabled} onChange={toggle} />
    <span><strong>Session #{props.session.sessionIndex}</strong><small>{formatDate(props.session.startedAt)} · {formatCountryLabel(props.session)} · {formatDuration(props.session.durationMs)}</small></span>
    <PlatformBadge code={props.session.platformCode} />
  </label>;
}

function renderInstallationOption(installation: InstallationSummary): React.JSX.Element {
  return <option key={installation.installationId} value={installation.installationId}>{formatPlatformNames(installation.platformCodes)} · {formatCountryLabel(installation)} · {shortId(installation.installationId)} · {installation.sessionCount} sessions · {formatDate(installation.lastSeenAt)}</option>;
}

function renderSessionOption(session: SessionSummary): React.JSX.Element {
  return <option key={session.sessionId} value={session.sessionId}>{formatPlatformName(session.platformCode)} · {formatCountryLabel(session)} · #{session.sessionIndex} · {formatDate(session.startedAt)} · {formatDuration(session.durationMs)} · {session.appVersion}</option>;
}

function renderSessionContext(session: SessionSummary): React.JSX.Element {
  return <div className="analysis-session-context" key={session.sessionId}>
    <span><strong>Session #{session.sessionIndex}</strong><code title={session.sessionId}>{shortId(session.sessionId)}</code></span>
    <SessionCountry countryCode={session.countryCode} countryCodes={session.countryCodes} />
    <PlatformBadge code={session.platformCode} />
    <small>{session.appVersion} · {formatDuration(session.durationMs)} · {formatNumber(session.eventCount)} events</small>
  </div>;
}

function formatCountryLabel(value: { countryCode: string | null }): string {
  return value.countryCode === null ? 'Unknown country' : formatCountryName(value.countryCode);
}

function readAnalysisLocation(): AnalysisLocationState {
  const query = new URLSearchParams(window.location.search);
  const mode = query.get('analysisMode') === 'comparison' ? 'comparison' : 'session';
  const requestedTab = query.get('analysisTab');
  const tab = isAnalysisTab(requestedTab) ? requestedTab : 'colony';
  const platformCodes = readPlatformCodes(query.get('analysisPlatforms'));
  const installationId = query.get('analysisInstall');
  const sessionIds = (query.get('analysisSessions') ?? '').split(',').filter(hasText).slice(0, MAXIMUM_COMPARISON_SESSIONS);
  return { mode, tab, platformCodes, installationId, sessionIds };
}

function writeAnalysisLocation(update: AnalysisLocationUpdate, method: 'push' | 'replace'): void {
  const url = new URL(window.location.href);
  url.searchParams.set('view', 'analysis');
  if (update.mode !== undefined) url.searchParams.set('analysisMode', update.mode);
  if (update.tab !== undefined) url.searchParams.set('analysisTab', update.tab);
  if (update.platformCodes !== undefined) setOptionalParameter(url, 'analysisPlatforms', update.platformCodes.join(','));
  if (update.installationId !== undefined) setOptionalParameter(url, 'analysisInstall', update.installationId);
  if (update.sessionIds !== undefined) setOptionalParameter(url, 'analysisSessions', update.sessionIds.join(','));
  if (method === 'push') {
    window.history.pushState({}, '', url);
  } else {
    window.history.replaceState({}, '', url);
  }
}

function setOptionalParameter(url: URL, key: string, value: string | null): void {
  if (value === null || value === '') {
    url.searchParams.delete(key);
  } else {
    url.searchParams.set(key, value);
  }
}

function isAnalysisTab(value: string | null): value is AnalysisTab {
  return value === 'colony' || value === 'economy' || value === 'construction' || value === 'logistics';
}

function buildEventLookup(datasets: AnalysisSessionDataset[]): ReadonlyMap<string, AnalyticsEventRecord> {
  const lookup = new Map<string, AnalyticsEventRecord>();
  for (const dataset of datasets) for (const event of dataset.events) lookup.set(event.eventId, event);
  return lookup;
}

function filterInstallationsByPlatform(installations: InstallationSummary[], platformCodes: readonly number[]): InstallationSummary[] {
  if (platformCodes.length === 0) return installations;
  return installations.filter((installation) => installation.platformCodes.some((platformCode) => platformCodes.includes(platformCode)));
}

function collectPlatformFilterCodes(installations: InstallationSummary[], selectedCodes: readonly number[]): number[] {
  const platformCodes = new Set<number>(listKnownPlatformCodes());
  for (const installation of installations) for (const platformCode of installation.platformCodes) platformCodes.add(platformCode);
  for (const platformCode of selectedCodes) platformCodes.add(platformCode);
  return [...platformCodes].sort(sortNumbers);
}

function countInstallationsForPlatform(installations: InstallationSummary[], platformCode: number): number {
  let count = 0;
  for (const installation of installations) if (installation.platformCodes.includes(platformCode)) count++;
  return count;
}

function describePlatformFilter(selectedCodes: readonly number[], matchingCount: number, totalCount: number): string {
  if (selectedCodes.length === 0) return `${totalCount} installs available across all platforms`;
  return `${matchingCount} of ${totalCount} installs · ${formatPlatformNames(selectedCodes)}`;
}

function readPlatformCodes(value: string | null): number[] {
  if (value === null || value === '') return [];
  const platformCodes = new Set<number>();
  for (const token of value.split(',')) {
    if (!/^\d{1,5}$/.test(token)) continue;
    const platformCode = Number(token);
    if (Number.isSafeInteger(platformCode) && platformCode <= 65535) platformCodes.add(platformCode);
  }
  return [...platformCodes].sort(sortNumbers);
}

function sortNumbers(left: number, right: number): number { return left - right; }

function isSessionSummary(value: SessionSummary | undefined): value is SessionSummary { return value !== undefined; }
function readSessionId(session: SessionSummary): string { return session.sessionId; }
function hasText(value: string): boolean { return value !== ''; }
function shortId(value: string): string { return value.slice(0, 8); }
function formatNumber(value: number): string { return new Intl.NumberFormat().format(value); }
function formatDate(value: string): string { return formatDateTime(value); }
function formatDuration(milliseconds: number): string { const seconds = Math.round(milliseconds / 1000); return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`; }
